import {
  type FullSalesOrderInfo,
  type PatchedUpdateSalesOrderDTO,
  PricingTypeEnum,
  type TransportMethodEnum,
  useApiOrdersV1SalesOrdersRetrieve,
  WhoFixesDateEnum,
} from 'api';
import { dayjs } from 'date';
import { Form, type InferType, useForm, yupResolver } from 'form';
import { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, AlertTitle, Box, Button, ConfirmationDialog, Page, Section } from 'ui';

import { SalesOrderFormSections } from '../../components';
import { SalesOrderFormContextProvider } from '../../contexts/SalesOrderFormContext';
import { useUpdateSalesOrder } from '../../hooks';
import { updateSalesOrderSchema } from '../../schemas/updateSalesOrderSchema';
import {
  convertOrderMaterialComponentValuesToForm,
  convertToOrderInputMaterialComponents,
} from '../../utils/materialComponentValue';
import { scrollToFirstError } from '../../utils/scrollToFirstError';

export type SalesOrderUpdateFormValues = InferType<typeof updateSalesOrderSchema>;

const getSalesOrderFormDefaults = (salesOrder: FullSalesOrderInfo): SalesOrderUpdateFormValues => {
  return {
    // @ts-expect-error This errors because the expected type is number[] but the autocomplete actually uses the whole carrier object and I haven't figured out how to type it properly, but at runtime it works
    allowed_shipping_lines_ids: salesOrder?.allowed_shipping_lines,
    bank_details_id: salesOrder.bank_details?.id ?? undefined,
    basel_code_override: salesOrder.basel_code_override,
    bill_type: salesOrder.bill_type ?? undefined,
    buyer_agent_id: salesOrder.buyer_agent?.id,
    buyer_currency: salesOrder.buyer_currency,
    buyer_material_component_values: convertOrderMaterialComponentValuesToForm(
      salesOrder.material_component_values || []
    ),
    buyer_material_spec_id: salesOrder.buyer_material_spec_id,
    cash_or_three_month: salesOrder.cash_or_three_month ?? undefined,
    collection_site_id: salesOrder.collection_site?.id,
    colour_id: salesOrder.colour?.id,
    comments: salesOrder.comments,
    consignee_id: salesOrder.consignee?.id,
    delivery_site_id: salesOrder.delivery_site?.id,
    deposit_percentage: salesOrder.deposit_percentage ?? '0',
    dirt_level: salesOrder.dirt_level ?? undefined,
    due_date_direction: salesOrder?.due_date_offset && salesOrder.due_date_offset > 0 ? 1 : -1,
    due_date_offset: salesOrder?.due_date_offset ?? 0,
    end_processor_id: salesOrder.end_processor.id,
    external_reference_number: salesOrder.external_reference_number,
    final_destination_site_id: salesOrder.final_destination_site?.id ?? 0,
    fixed_date: salesOrder.fixing_date ?? undefined,
    fixing_date: salesOrder.fixing_date ?? undefined,
    fixing_date_end_event: salesOrder.fixing_date_end_event ?? undefined,
    fixing_date_end_offset: salesOrder.fixing_date_end_offset ?? undefined,
    fixing_date_start_event: salesOrder.fixing_date_start_event ?? undefined,
    fixing_date_start_offset: salesOrder.fixing_date_start_offset ?? undefined,
    form_id: salesOrder.form?.id,
    free_time_required: salesOrder.free_time_required,
    grade_id: salesOrder.grade?.id ?? 0,
    has_caps_and_labels: salesOrder.has_caps_and_labels ?? undefined,
    icd_id: salesOrder.icd?.id,
    incoterm: salesOrder.incoterm,
    index: salesOrder.index || undefined,
    internal_notes: salesOrder.internal_notes,
    invoice_finance_provider: salesOrder.invoice_finance_provider ?? undefined,
    is_food_grade: salesOrder.is_food_grade ?? false,
    is_trial_order: salesOrder.is_trial_order ?? false,
    is_variable_pricing: salesOrder.pricing_type === PricingTypeEnum.INDEX,
    marks_and_numbers: salesOrder.marks_and_numbers ?? undefined,
    material_colour_id: salesOrder.allowed_material_spec?.colour?.id || undefined,
    material_customs_name_override: salesOrder.material_customs_name_override,
    material_ewc_code_override: salesOrder.material_ewc_code_override,
    material_hs_code_override: salesOrder.material_hs_code_override,
    material_id: salesOrder.allowed_material_spec?.material?.id ?? 0,
    material_spec_id: salesOrder.allowed_material_spec?.id ?? 0,
    max_number_of_shipments: salesOrder.max_number_of_shipments ?? undefined,
    notify_party_id: salesOrder.notify_party?.id ?? undefined,
    number_of_loads: Math.ceil(Number(salesOrder.tonnes) / Number(salesOrder.tonnes_per_load)),
    origin_id: salesOrder.origin?.id,
    payment_event: salesOrder.payment_event ?? undefined,
    payment_method: salesOrder.payment_method!,
    payment_must_be_received_before_safi_releases_the_documents:
      salesOrder.payment_must_be_received_before_safi_releases_the_documents ?? false,
    percentage_fines: salesOrder?.percentage_fines ?? undefined,
    percentage_light_blue: salesOrder?.percentage_light_blue ?? undefined,
    percentage_reds: salesOrder?.percentage_reds ?? undefined,
    port_of_discharge_id: salesOrder.port_of_discharge?.id,
    port_of_loading_id: salesOrder.port_of_loading?.id,
    price_as_percentage_of_index: salesOrder.price_as_percentage_of_index ?? undefined,
    price_per_tonne: salesOrder.price_per_tonne,
    price_point: salesOrder.price_point ?? undefined,
    purchaser_id: salesOrder.purchaser.id,
    safi_vat: salesOrder.safi_vat ?? undefined,
    shipment_deadline_days: salesOrder.shipment_deadline_days ?? undefined,
    shipper_id: salesOrder.shipper?.id,
    shipping_instruction_notes: salesOrder.shipping_instruction_notes ?? undefined,
    site_material_id: salesOrder.site_material_id || undefined,
    target_delivery_date: salesOrder.target_delivery_date ?? '',
    third_party_commission_currency: salesOrder.third_party_commission_currency,
    third_party_commission_per_tonne: salesOrder.third_party_commission_per_tonne ?? '0',
    tonnage_variance: salesOrder.tonnage_variance ?? '10',
    tonnes: salesOrder.tonnes,
    tonnes_per_load: salesOrder.tonnes_per_load ?? '',
    // We need to cast here as this is a required field, but it wasn't previously so not all SOs have it set
    transport_method: salesOrder?.transport_method ?? ('' as TransportMethodEnum),
    who_fixes_date: salesOrder.who_fixes_date ?? undefined,
  };
};

export const SalesOrderUpdate = () => {
  const { id: salesOrderId } = useParams();

  const { data: salesOrderData, isLoading } = useApiOrdersV1SalesOrdersRetrieve(salesOrderId!, {
    query: {
      enabled: salesOrderId !== undefined,
    },
  });

  const [fixedPriceDialogOpen, setFixedPriceDialogOpen] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const form = useForm<SalesOrderUpdateFormValues>({
    resolver: yupResolver(updateSalesOrderSchema),
    values: salesOrderData ? getSalesOrderFormDefaults(salesOrderData) : undefined,
  });

  const { mutate: updateSalesOrder, isPending } = useUpdateSalesOrder(form);

  const isVariablePricing = form.getValues('is_variable_pricing');
  const { watch } = form;
  const fixedPrice = watch('fixed_price');
  const dateFixed = watch('date_fixed');

  const onSubmit = (values: SalesOrderUpdateFormValues) => {
    if (!salesOrderData) {
      return;
    }
    const targetDeliveryDate = dayjs(values.target_delivery_date).format('YYYY-MM-DD');
    const pricingType = values.is_variable_pricing ? PricingTypeEnum.INDEX : PricingTypeEnum.FIXED;
    const fixingDate =
      values.who_fixes_date && values.fixing_date ? dayjs(values.fixing_date).format('YYYY-MM-DD') : undefined;
    const pricePerTonne = values.fixed_price ? values.fixed_price : values.price_per_tonne;
    const preliminaryPrice =
      pricingType === PricingTypeEnum.INDEX && pricingType !== salesOrderData?.pricing_type
        ? values.price_per_tonne
        : undefined;
    const date_fixed = values.date_fixed ? dayjs(values.date_fixed).format('YYYY-MM-DD') : undefined;

    const materialComponentValues = convertToOrderInputMaterialComponents(values.buyer_material_component_values || []);

    const orderData: PatchedUpdateSalesOrderDTO = {
      ...values,
      date_fixed,
      fixing_date: fixingDate,
      material_component_values: materialComponentValues,
      preliminary_price: preliminaryPrice,
      price_per_tonne: pricePerTonne,
      pricing_type: pricingType,
      target_delivery_date: targetDeliveryDate,
    };

    if (!values.is_variable_pricing) {
      (
        [
          'index',
          'price_as_percentage_of_index',
          'cash_or_three_month',
          'who_fixes_date',
          'fixing_date',
          'fixing_date_start_event',
          'fixing_date_start_offset',
          'fixing_date_end_event',
          'fixing_date_end_offset',
          'price_point',
        ] as const
      ).forEach((field) => {
        orderData[field] = undefined;
      });
    }

    if (values.who_fixes_date === WhoFixesDateEnum.CUSTOMER || values.who_fixes_date === WhoFixesDateEnum.SAFI) {
      orderData.fixing_date = undefined;
    } else {
      (
        [
          'fixing_date_start_event',
          'fixing_date_start_offset',
          'fixing_date_end_event',
          'fixing_date_end_offset',
        ] as const
      ).forEach((field) => {
        orderData[field] = undefined;
      });
    }

    updateSalesOrder({
      data: orderData,
      slug: salesOrderData.slug,
    });
  };

  const onError = () => {
    requestAnimationFrame(() => {
      scrollToFirstError();
    });
  };

  const handleSubmitButton = () => {
    if (fixedPrice && dateFixed) {
      setFixedPriceDialogOpen(true);
    } else {
      formRef.current?.requestSubmit();
    }
  };

  return !isLoading && salesOrderData ? (
    <Page>
      <Page.Divider />
      <Page.Title
        title={`Update sales order ${salesOrderData.slug}`}
        subtitle={`Please edit the sales order details for ${salesOrderData.slug}.`}
      />
      <Section>
        <Box my={2}>
          {isVariablePricing ? (
            <Alert severity="info">
              <AlertTitle>Variable priced order</AlertTitle>
              To fix price, edit the Fixed price and Fixed date fields.
            </Alert>
          ) : null}
        </Box>
        <SalesOrderFormContextProvider
          variant="update"
          documents={salesOrderData.documents ?? []}
          defaultValues={getSalesOrderFormDefaults(salesOrderData)}
        >
          <Form {...form} onSubmit={onSubmit} onError={onError} formRef={formRef} devtool={false}>
            <SalesOrderFormSections />
            <Box display="flex" justifyContent="end" alignItems="center" paddingTop={4}>
              <ConfirmationDialog
                open={fixedPriceDialogOpen}
                title="Confirm Fixed Price"
                message="You are about to fix the price of this order. This action will lock in the current price and date. Do you want to proceed?"
                handleCancel={() => {
                  setFixedPriceDialogOpen(false);
                }}
                handleConfirm={() => {
                  setFixedPriceDialogOpen(false);
                  formRef.current?.requestSubmit();
                }}
              />
              <Button size="large" loading={isPending} onClick={handleSubmitButton}>
                Submit
              </Button>
            </Box>
          </Form>
        </SalesOrderFormContextProvider>
      </Section>
    </Page>
  ) : null;
};
